import React, { useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import AuthTabs from './components/AuthTabs';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import RegisterPitch from './components/RegisterPitch';

const AUTH_TABS = ['login', 'register'];

const parseAuthTab = (value) => {
  const key = String(value || '').trim().toLowerCase();
  if (key === 'register' || key === 'signup' || key === 'sign-up' || key === 'sign_up') {
    return 'register';
  }
  return 'login';
};

const Authentication = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseAuthTab(searchParams.get('tab'));

  const setActiveTab = useCallback((tab) => {
    const nextTab = AUTH_TABS.includes(tab) ? tab : parseAuthTab(tab);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', nextTab === 'register' ? 'signup' : 'login');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <>
      <Helmet>
        <title>{t('auth.pageTitle')}</title>
        <meta name="description" content={t('auth.pageDescription')} />
      </Helmet>
      <div className="min-h-screen bg-background flex items-center justify-center p-4 md:p-6 lg:p-8 relative">
        <div className="w-full max-w-md lg:max-w-lg">
          {/* Logo and Header */}
          <div className="text-center mb-6 md:mb-8">
            <img
              src="/assets/brand/hireportal-logo.svg"
              alt="HirePortal"
              className="mx-auto mb-4 h-14 w-auto md:mb-6 md:h-16"
            />
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
              {activeTab === 'register' ? t('auth.signupTitle') : t('auth.welcome')}
            </h1>
            {activeTab !== 'register' && (
              <p className="text-sm md:text-base text-muted-foreground">
                {t('auth.welcomeSubtitle')}
              </p>
            )}
          </div>

          {activeTab === 'register' && <RegisterPitch />}

          {/* Authentication Card */}
          <div className="bg-card rounded-xl md:rounded-2xl shadow-elevation-lg border border-border p-4 md:p-5 lg:p-6">
            <AuthTabs activeTab={activeTab} onTabChange={setActiveTab} />

            {activeTab === 'login' ? <LoginForm /> : <RegisterForm />}
          </div>

          {/* Footer Note */}
          <div className="mt-6 md:mt-8 text-center">
            <p className="text-xs md:text-sm text-muted-foreground">
              {t('auth.agreePrefix')}{' '}
              <Link to="/terms" className="text-primary hover:text-accent transition-colors underline underline-offset-2">
                {t('auth.terms')}
              </Link>{' '}
              {t('auth.and')}{' '}
              <Link to="/privacy" className="text-primary hover:text-accent transition-colors underline underline-offset-2">
                {t('auth.privacy')}
              </Link>
            </p>
          </div>

          {/* Copyright */}
          <div className="mt-6 md:mt-8 text-center">
            <p className="text-xs text-muted-foreground">
              {t('auth.copyright', { year: new Date()?.getFullYear() })}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Authentication;
